import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bot, RefreshCw, Loader2, ShieldAlert, CheckCircle2, Clock,
  AlertTriangle, PlayCircle, ArrowRight, Activity, Users, Zap,
  Inbox, ArrowUpRight, Layers, HeartHandshake, ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { isFeatureLive } from '../lib/caraloom_v2_ia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANNER_NAV = [
  { icon: Layers, label: 'Planner Dashboard', href: '/planner/dashboard' },
  { icon: Bot, label: 'Orchestrator', active: true },
  { icon: HeartHandshake, label: 'Advance Care Planning', href: '/planner/acp' },
];

// State → pipeline step index (0-based)
const PIPELINE_STEPS = [
  { key: 'REFERRAL_RECEIVED', label: 'Received' },
  { key: 'RISK_SCORED', label: 'Scored' },
  { key: 'MATCHED', label: 'Matched' },
  { key: 'OFFERS_BROADCAST', label: 'Offered' },
  { key: 'COVERAGE_HOLD', label: 'Coverage' },
  { key: 'ORDERS_DRAFTED', label: 'Orders' },
  { key: 'AWAITING_ACCEPT', label: 'Awaiting' },
  { key: 'ACCEPTED', label: 'Accepted' },
  { key: 'FIRST_VISIT_COMMITTED', label: 'Visit set' },
];

function stepIndexFor(state) {
  const idx = PIPELINE_STEPS.findIndex((s) => s.key === state);
  return idx >= 0 ? idx : 0;
}

const URGENCY_TONE = {
  stat: 'bg-red-50 text-red-700 border-red-200',
  urgent: 'bg-amber-50 text-amber-700 border-amber-200',
  routine: 'bg-slate-100 text-slate-700 border-slate-200',
};

function fmtDays(d) {
  if (d == null) return '—';
  if (d < 1) return `${Math.round(d * 24)}h`;
  return `${d}d`;
}

export default function PlannerOrchestrator() {
  const { getAuthHeader } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningRef, setRunningRef] = useState(null);
  const [busyGate, setBusyGate] = useState(null);

  const featureOn = isFeatureLive('referrals.orchestrator');

  const load = useCallback(async () => {
    if (!featureOn) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/orchestrator/planner-inbox`, getAuthHeader());
      setData(r.data);
    } catch (e) {
      if (e.response?.status !== 402) {
        toast.error(e.response?.data?.detail || 'Could not load orchestrator inbox');
      }
    } finally { setLoading(false); }
  }, [getAuthHeader, featureOn]);

  const runLoop = useCallback(async (referralId) => {
    setRunningRef(referralId);
    try {
      const r = await axios.post(
        `${API}/orchestrator/referrals/${referralId}/run`, {}, getAuthHeader(),
      );
      const term = r.data?.trace?.terminal_state || r.data?.trace?.state;
      toast.success(`Loop finished — ${term}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run loop');
    } finally { setRunningRef(null); }
  }, [getAuthHeader, load]);

  const resolveGate = useCallback(async (traceId, gateId, decision) => {
    setBusyGate(gateId);
    try {
      await axios.post(
        `${API}/orchestrator/gates/${gateId}/approve`,
        { trace_id: traceId, decision },
        getAuthHeader(),
      );
      toast.success(`Gate ${decision}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resolve gate');
    } finally { setBusyGate(null); }
  }, [getAuthHeader, load]);

  const [advancingTrace, setAdvancingTrace] = useState(null);
  const [ackingTrace, setAckingTrace] = useState(null);
  const ackRepair = useCallback(async (traceId) => {
    setAckingTrace(traceId);
    try {
      await axios.post(
        `${API}/orchestrator/traces/${traceId}/ack-repair`,
        {},
        getAuthHeader(),
      );
      toast.success('Suggestion acknowledged');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not acknowledge suggestion');
    } finally { setAckingTrace(null); }
  }, [getAuthHeader, load]);

  const advanceWave = useCallback(async (traceId) => {
    setAdvancingTrace(traceId);
    try {
      const r = await axios.post(
        `${API}/orchestrator/traces/${traceId}/advance-wave`,
        { note: 'planner escalated to next wave' },
        getAuthHeader(),
      );
      const t = r.data?.summary;
      const term = r.data?.trace?.terminal_state;
      if (term === 'DECLINED_ALL') {
        toast.error('No more candidates — trace terminated as DECLINED_ALL');
      } else if (t?.waves_run) {
        toast.success(`Advanced to wave ${t.waves_run}`);
      } else {
        toast.success('Wave advanced');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not advance wave');
    } finally { setAdvancingTrace(null); }
  }, [getAuthHeader, load]);

  useEffect(() => { load(); }, [load]);

  if (!featureOn) {
    return (
      <DashboardShell
        navItems={PLANNER_NAV}
        title="Orchestrator"
        subtitle="Currently disabled for this environment."
        testId="planner-orchestrator-page"
      >
        <div className="cn-card p-8 text-center" data-testid="planner-orch-flag-off">
          <Bot className="w-10 h-10 mx-auto text-slate-400 mb-3" />
          <div className="cn-h3 mb-2">Orchestrator not enabled</div>
          <p className="text-cn-text-muted text-sm">
            Ask your CaraLoom admin to flip the <code>orchestrator</code> entitlement flag for
            your hospital org.
          </p>
        </div>
      </DashboardShell>
    );
  }

  if (loading) {
    return (
      <DashboardShell
        navItems={PLANNER_NAV}
        title="Orchestrator"
        subtitle="Autonomous discharge loop control room."
        testId="planner-orchestrator-page"
      >
        <div className="cn-card p-10 text-center" data-testid="planner-orch-loading">
          <Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" />
        </div>
      </DashboardShell>
    );
  }

  const kpis = data?.kpis || {};
  const activeTraces = data?.active_traces || [];
  const pendingGates = data?.pending_gates || [];
  const pendingReferrals = data?.pending_referrals || [];
  const repairBriefs = data?.repair_briefs || [];

  return (
    <DashboardShell
      navItems={PLANNER_NAV}
      title="Orchestrator"
      subtitle="Every discharge, closed autonomously. Approve a gate and the loop continues."
      testId="planner-orchestrator-page"
    >
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="planner-orch-kpis">
        <Kpi icon={Activity} label="Active loops" value={kpis.active_loops ?? 0} tone="sky" />
        <Kpi icon={Inbox} label="Pending gates" value={kpis.pending_gates ?? 0} tone={kpis.pending_gates ? 'red' : 'slate'} />
        <Kpi icon={CheckCircle2} label="Placed today" value={kpis.placed_today ?? 0} tone="emerald" />
        <Kpi icon={AlertTriangle} label="Escalated today" value={kpis.escalated_today ?? 0} tone={kpis.escalated_today ? 'amber' : 'slate'} />
        <Kpi icon={Users} label="Pending referrals" value={kpis.pending_referrals ?? 0} tone={kpis.pending_referrals ? 'sky' : 'slate'} />
      </div>

      {/* Cara-authored repair briefs — LLM Repair Agent surface (iter #126) */}
      {repairBriefs.length > 0 && (
        <Section
          title="Cara has a suggestion"
          testId="planner-orch-repair-briefs"
          icon={<Sparkles className="w-4 h-4 text-violet-600" />}
          hint="The LLM Repair Agent analyzed traces that hit a terminal problem state and drafted a next step. Ack once you've acted."
        >
          <div className="space-y-2">
            {repairBriefs.map((b) => (
              <div
                key={b.trace_id}
                className="border border-violet-200 bg-violet-50/40 rounded px-3 py-2"
                data-testid={`planner-repair-brief-${b.trace_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[0.65rem] font-semibold uppercase text-violet-700 tracking-wider">
                        {b.terminal_state}
                      </span>
                      <code className="text-xs text-cn-text-muted">
                        {b.referral_id?.slice(0, 14)}…
                      </code>
                      {b.urgency && (
                        <span className={`px-1.5 py-0.5 rounded border text-[0.65rem] uppercase ${URGENCY_TONE[b.urgency] || URGENCY_TONE.routine}`}>
                          {b.urgency}
                        </span>
                      )}
                      {b.applied && (
                        <span className="px-1.5 py-0.5 rounded border text-[0.65rem] bg-emerald-50 text-emerald-700 border-emerald-200">
                          auto-applied
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-800 leading-snug">
                      {b.brief}
                    </div>
                    {b.action && (
                      <div className="text-[0.65rem] text-cn-text-muted mt-1">
                        Suggested action · <code>{b.action}</code>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      onClick={() => nav(`/admin/orchestrator?trace=${b.trace_id}`)}
                      className="cn-btn cn-btn-ghost !py-1 !text-xs"
                      data-testid={`planner-repair-open-${b.trace_id}`}
                    >
                      Open trace <ArrowUpRight className="w-3 h-3" />
                    </Button>
                    <Button
                      onClick={() => ackRepair(b.trace_id)}
                      disabled={ackingTrace === b.trace_id}
                      className="cn-btn cn-btn-primary !py-1 !text-xs"
                      data-testid={`planner-repair-ack-${b.trace_id}`}
                    >
                      {ackingTrace === b.trace_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3 h-3" />
                      )}
                      Acknowledge
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Pending gate inbox — highest priority */}
      {pendingGates.length > 0 && (
        <Section
          title="Awaiting your sign-off"
          testId="planner-orch-gates"
          icon={<Clock className="w-4 h-4 text-red-600" />}
        >
          <div className="space-y-2">
            {pendingGates.map((g) => (
              <div
                key={g.gate_id}
                className="flex items-center justify-between border border-red-200 bg-red-50/40 rounded px-3 py-2"
                data-testid={`planner-gate-${g.gate_id}`}
              >
                <div className="text-sm flex-1">
                  <div className="font-medium">{g.gate}</div>
                  <div className="text-xs text-cn-text-muted">
                    Referral <code>{g.referral_id?.slice(0, 12)}…</code>
                    {g.urgency && (
                      <span className={`ml-2 px-1.5 py-0.5 rounded border text-[0.65rem] uppercase ${URGENCY_TONE[g.urgency] || URGENCY_TONE.routine}`}>
                        {g.urgency}
                      </span>
                    )}
                  </div>
                  {g.note && <div className="text-xs text-cn-text-muted mt-0.5">{g.note}</div>}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => resolveGate(g.trace_id, g.gate_id, 'approved')}
                    disabled={busyGate === g.gate_id}
                    className="cn-btn cn-btn-primary !py-1 !text-xs"
                    data-testid={`planner-approve-${g.gate_id}`}
                  >
                    {busyGate === g.gate_id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Approve
                  </Button>
                  <Button
                    onClick={() => resolveGate(g.trace_id, g.gate_id, 'rejected')}
                    disabled={busyGate === g.gate_id}
                    className="cn-btn cn-btn-ghost !py-1 !text-xs"
                    data-testid={`planner-reject-${g.gate_id}`}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Pending referrals — one-click run */}
      {pendingReferrals.length > 0 && (
        <Section
          title="Referrals ready for orchestration"
          testId="planner-orch-pending"
          icon={<Zap className="w-4 h-4 text-cn-primary" />}
          hint="One-click launches the full discharge loop. The agent takes over from here."
        >
          <div className="grid md:grid-cols-2 gap-2">
            {pendingReferrals.map((r) => (
              <div
                key={r.referral_id}
                className="border border-slate-200 rounded px-3 py-2 flex items-center justify-between"
                data-testid={`planner-pending-ref-${r.referral_id}`}
              >
                <div className="text-sm min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs text-cn-text-muted">{r.referral_id?.slice(0, 14)}…</code>
                    {r.urgency && (
                      <span className={`px-1.5 py-0.5 rounded border text-[0.65rem] uppercase ${URGENCY_TONE[r.urgency] || URGENCY_TONE.routine}`}>
                        {r.urgency}
                      </span>
                    )}
                    <span className="text-[0.65rem] text-cn-text-muted">
                      {fmtDays(r.days_since_intake)} since intake
                    </span>
                  </div>
                  {r.ordered_services?.length > 0 && (
                    <div className="text-xs text-cn-text-muted mt-1 truncate">
                      {r.ordered_services.join(', ')}
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => runLoop(r.referral_id)}
                  disabled={runningRef === r.referral_id}
                  className="cn-btn cn-btn-primary !py-1 !text-xs shrink-0"
                  data-testid={`planner-run-${r.referral_id}`}
                >
                  {runningRef === r.referral_id
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <PlayCircle className="w-3 h-3" />}
                  Run loop
                </Button>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Active loops — pipeline visualization */}
      <Section
        title="Active loops"
        testId="planner-orch-active"
        icon={<Activity className="w-4 h-4 text-cn-primary" />}
        actions={
          <Button onClick={load} className="cn-btn cn-btn-ghost !py-1 !text-xs" data-testid="planner-orch-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        }
      >
        {activeTraces.length === 0 ? (
          <div className="text-center py-8" data-testid="planner-orch-active-empty">
            <Bot className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <div className="text-sm text-cn-text-muted">
              No active loops. Kick one off from the pending list above.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {activeTraces.map((t) => (
              <ActiveTrace
                key={t.id}
                trace={t}
                onOpen={() => nav(`/admin/orchestrator?trace=${t.id}`)}
                onAdvance={() => advanceWave(t.id)}
                advancing={advancingTrace === t.id}
              />
            ))}
          </div>
        )}
      </Section>
    </DashboardShell>
  );
}

function ActiveTrace({ trace, onOpen, onAdvance, advancing }) {
  const stepIdx = stepIndexFor(trace.state);
  const mockedCount = trace.integrations_mocked?.length || 0;
  const wavesRun = trace.waves_run || 0;
  const queueRemaining = trace.candidate_queue_remaining || 0;
  const canAdvance = queueRemaining > 0 && !trace.terminal_state;

  return (
    <div
      className="border border-slate-200 rounded p-3 hover:bg-slate-50"
      data-testid={`planner-active-trace-${trace.id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div
          className="flex items-center gap-2 flex-wrap min-w-0 cursor-pointer"
          onClick={onOpen}
        >
          <code className="text-xs text-cn-text-muted">{trace.referral_id?.slice(0, 14)}…</code>
          {trace.urgency && (
            <span className={`px-1.5 py-0.5 rounded border text-[0.65rem] uppercase ${URGENCY_TONE[trace.urgency] || URGENCY_TONE.routine}`}>
              {trace.urgency}
            </span>
          )}
          {wavesRun > 0 && (
            <span
              className="inline-flex items-center gap-1 text-cyan-800 text-[0.65rem] px-1.5 py-0.5 rounded bg-cyan-50 border border-cyan-200"
              data-testid={`planner-wave-badge-${trace.id}`}
              title={`${wavesRun} offer wave${wavesRun === 1 ? '' : 's'} attempted • ${queueRemaining} candidates in queue`}
            >
              <Layers className="w-3 h-3" /> Wave {wavesRun}
              {queueRemaining > 0 && <span className="opacity-70">· {queueRemaining} left</span>}
            </span>
          )}
          <span className="text-[0.65rem] text-cn-text-muted">
            {fmtDays(trace.days_since_intake)} since intake
          </span>
          {trace.coverage && (
            trace.state === 'COVERAGE_HOLD' ? (
              <span
                className="inline-flex items-center gap-1 text-red-700 text-[0.65rem] px-1.5 py-0.5 rounded bg-red-50 border border-red-200"
                data-testid={`planner-coverage-hold-${trace.id}`}
                title={(trace.coverage.blocking_reasons || []).join('; ')}
              >
                <ShieldAlert className="w-3 h-3" /> Coverage hold
              </span>
            ) : trace.coverage.passed ? (
              <span
                className="inline-flex items-center gap-1 text-emerald-700 text-[0.65rem] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200"
                data-testid={`planner-coverage-pass-${trace.id}`}
              >
                <ShieldCheck className="w-3 h-3" /> Coverage ✓
              </span>
            ) : null
          )}
          {(trace.order_document_ids?.length || 0) > 0 && (
            <span
              className="inline-flex items-center gap-1 text-teal-800 text-[0.65rem] px-1.5 py-0.5 rounded bg-teal-50 border border-teal-200"
              data-testid={`planner-orders-badge-${trace.id}`}
              title="F2F, 485 POC, NOA, consent drafted and awaiting signature"
            >
              {trace.order_document_ids.length} orders drafted
            </span>
          )}
          {mockedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700 text-[0.65rem] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200">
              <ShieldAlert className="w-3 h-3" /> {mockedCount} mocked
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canAdvance && (
            <Button
              onClick={(e) => { e.stopPropagation(); onAdvance(); }}
              disabled={advancing}
              className="cn-btn cn-btn-ghost !py-1 !text-xs"
              title="Expire the current offer and promote the next-best candidate"
              data-testid={`planner-advance-wave-${trace.id}`}
            >
              {advancing
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <ArrowRight className="w-3 h-3" />}
              Advance
            </Button>
          )}
          <ArrowUpRight
            className="w-4 h-4 text-slate-400 cursor-pointer"
            onClick={onOpen}
          />
        </div>
      </div>

      {/* Pipeline */}
      <div
        className="flex items-center gap-1 cursor-pointer"
        onClick={onOpen}
        data-testid={`planner-pipeline-${trace.id}`}
      >
        {PIPELINE_STEPS.map((s, i) => {
          const filled = i <= stepIdx;
          const isCurrent = i === stepIdx;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center min-w-0">
                <div className={`w-3 h-3 rounded-full ${
                  isCurrent ? 'bg-cn-primary ring-4 ring-cn-primary/20'
                  : filled ? 'bg-cn-primary' : 'bg-slate-200'
                }`} />
                <div className={`text-[0.55rem] mt-0.5 whitespace-nowrap ${
                  isCurrent ? 'font-semibold text-cn-primary'
                  : filled ? 'text-cn-text' : 'text-slate-400'
                }`}>{s.label}</div>
              </div>
              {i < PIPELINE_STEPS.length - 1 && (
                <div className={`flex-1 h-px ${
                  i < stepIdx ? 'bg-cn-primary' : 'bg-slate-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function Section({ title, icon, actions, children, testId, hint }) {
  return (
    <div className="cn-card p-4 mb-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-semibold">
          {icon}
          <span>{title}</span>
        </div>
        {actions}
      </div>
      {hint && <p className="text-xs text-cn-text-muted mb-3">{hint}</p>}
      {children}
    </div>
  );
}

const TONE_MAP = {
  sky: 'bg-sky-50 text-sky-700 border-sky-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  slate: 'bg-slate-50 text-slate-700 border-slate-200',
};

function Kpi({ icon: Icon, label, value, tone = 'slate' }) {
  return (
    <div className={`cn-card p-3 flex items-center gap-3 border ${TONE_MAP[tone]}`}>
      <Icon className="w-5 h-5 shrink-0 opacity-80" />
      <div>
        <div className="text-[0.6rem] uppercase tracking-wide opacity-70">{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </div>
    </div>
  );
}
